import React, { createContext, useCallback, useState } from 'react';
import { STORAGE } from '~/constants';
import { AuthContextData, AuthState, LogonHist, User } from './types';
import apiSSO from '~/utils/sso';
import { queryClient } from '~/utils/react-query';
import axiosPrivate from '~/utils/axiosPrivate';

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData,
);

export const AuthProvider: React.FC = ({ children }) => {
  const loadRedirectAuthState = () => {
    const params = new URLSearchParams(window.location.search);
    const access = params.get('access');
    const refresh = params.get('refresh');
    const user = params.get('user');
    if (access && refresh && user) {
      const tokenInfo: User = JSON.parse(atob(user));

      const userPayload = {
        ...tokenInfo,
        type: tokenInfo.type === 'EXTERNO' ? 'Cliente' : 'Interno',
      } as User;

      localStorage.setItem(`${STORAGE}:user`, JSON.stringify(userPayload));
      localStorage.setItem(`${STORAGE}:token`, access);
      localStorage.setItem(`${STORAGE}:refreshToken`, refresh);

      apiSSO
        .post<LogonHist>('/auth/hist', {
          nm_loginusuario: userPayload.name,
          dh_entradaportal: new Date().toISOString(),
        })
        .then(logonResponse => {
          localStorage.setItem(
            `${STORAGE}:idLogon`,
            String(logonResponse.data.id_registrotempo),
          );
        });

      params.delete('access');
      params.delete('refresh');
      params.delete('user');
    }
  };

  const [data, setData] = useState<AuthState>(() => {
    loadRedirectAuthState();

    const token = localStorage.getItem(`${STORAGE}:token`);
    const refreshToken = localStorage.getItem(`${STORAGE}:refreshToken`);
    const user = localStorage.getItem(`${STORAGE}:user`);

    if (token && refreshToken && user) {
      return { token, refreshToken, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const updateUser = useCallback((user: User) => {
    const userData = localStorage.getItem(`${STORAGE}:user`);

    const updatedUser = { ...JSON.parse(userData || ''), ...user };

    setData(prev => ({ ...prev, user: updatedUser }));
    localStorage.setItem(`${STORAGE}:user`, JSON.stringify(updatedUser));
  }, []);

  const isStorageValid = useCallback(() => {
    const token = localStorage.getItem(`${STORAGE}:token`);
    const refreshToken = localStorage.getItem(`${STORAGE}:refreshToken`);
    const user = localStorage.getItem(`${STORAGE}:user`);

    return !!token && !!refreshToken && !!user;
  }, []);

  const signIn = useCallback(async ({ username, password }) => {
    queryClient.removeQueries();
    const response = await axiosPrivate.post(
      'auth/signin',
      {
        username,
        password,
      },
      {
        headers: {
          Authorization: '',
        },
      },
    );

    const { auth_otp, auth_token, multifator_habilitado } = response.data;

    const userPayload = {
      ...auth_token?.user,
      type: auth_token?.type === 'EXTERNO' ? 'Cliente' : 'Interno',
    } as User;

    !!auth_otp && sessionStorage.setItem(`${STORAGE}:otpToken`, auth_otp);
    !!multifator_habilitado &&
      sessionStorage.setItem(`${STORAGE}:multifator`, multifator_habilitado);

    if (auth_token) {
      localStorage.setItem(`${STORAGE}:token`, auth_token.access);
      localStorage.setItem(`${STORAGE}:refreshToken`, auth_token.refresh);
      localStorage.setItem(`${STORAGE}:user`, JSON.stringify(userPayload));

      const logonResponse = await apiSSO.post<LogonHist>('/auth/hist', {
        nm_loginusuario: username,
        dh_entradaportal: new Date().toISOString(),
      });

      localStorage.setItem(
        `${STORAGE}:idLogon`,
        String(logonResponse.data.id_registrotempo),
      );
    }

    setData({
      token: auth_token?.access,
      refreshToken: auth_token?.refresh,
      user: userPayload,
    });
  }, []);

  const verifyCode = useCallback(async ({ code }) => {
    queryClient.removeQueries();
    const otpToken = sessionStorage.getItem(`${STORAGE}:otpToken`);
    const response = await axiosPrivate.post(
      'auth/code',
      {
        code_token: otpToken,
        code,
      },
      {
        headers: {
          Authorization: '',
        },
      },
    );

    const { access: token, refresh: refreshToken, type, user } = response.data;

    const userPayload = {
      ...user,
      type: type === 'EXTERNO' ? 'Cliente' : 'Interno',
    } as User;

    if (token && token !== undefined) {
      clearSessionStorage();
      localStorage.setItem(`${STORAGE}:token`, token);
      localStorage.setItem(`${STORAGE}:refreshToken`, refreshToken);
      localStorage.setItem(`${STORAGE}:user`, JSON.stringify(userPayload));
      const logonResponse = await apiSSO.post<LogonHist>('/auth/hist', {
        nm_loginusuario: userPayload.login,
        dh_entradaportal: new Date().toISOString(),
      });

      localStorage.setItem(
        `${STORAGE}:idLogon`,
        String(logonResponse.data.id_registrotempo),
      );
    }
    setData({ token, refreshToken, user: userPayload });
  }, []);

  const resendEmail = useCallback(async () => {
    const token = sessionStorage.getItem(`${STORAGE}:otpToken`);
    queryClient.removeQueries();
    const response = await axiosPrivate.post(
      'auth/resend_auth_token',
      {
        code_token: token,
      },
      {
        headers: {
          Authorization: '',
        },
      },
    );
    const { auth_otp } = response.data;
    if (auth_otp) {
      sessionStorage.setItem(`${STORAGE}:otpToken`, auth_otp);
    }
  }, []);

  const clearSessionStorage = () => {
    sessionStorage.removeItem(`${STORAGE}:otpToken`);
    sessionStorage.removeItem(`${STORAGE}:multifator`);
  };
  const clearLocalStorage = () => {
    localStorage.removeItem(`${STORAGE}:token`);
    localStorage.removeItem(`${STORAGE}:refreshToken`);
    localStorage.removeItem(`${STORAGE}:user`);
    localStorage.removeItem(`${STORAGE}:idLogon`);
    localStorage.removeItem(`${STORAGE}:alertViewed`);
    setData({} as AuthState);
  };

  const signOut = useCallback(async () => {
    const idLogon = localStorage.getItem(`${STORAGE}:idLogon`);

    if (idLogon) {
      await apiSSO
        .put(`/auth/hist/${idLogon}`, {
          dh_saidaportal: new Date().toISOString(),
        })
        .then(() => {
          clearLocalStorage();
          clearSessionStorage();
        })
        .catch(err => console.error(err));
    }

    queryClient.removeQueries();
  }, []);

  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem(`${STORAGE}:token`);

    return !!token;
  }, []);

  const isCode2FAPending = useCallback(() => {
    const tokenOtp = sessionStorage.getItem(`${STORAGE}:otpToken`);

    return !!tokenOtp;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        isStorageValid,
        updateUser,
        clearLocalStorage,
        clearSessionStorage,
        isAuthenticated,
        isCode2FAPending,
        verifyCode,
        resendEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
